// © 2025 Ananda Kumar. All rights reserved.
// Unauthorized copying of this file, via any medium, is strictly prohibited.

import { Component, DestroyRef, inject, OnInit, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import confetti from 'canvas-confetti';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { ConfigurationService } from '../../../services/configuration.service';

@Component({
  selector: 'app-countdown',
  templateUrl: './countdown.component.html',
  styleUrls: ['./countdown.component.css'],
  standalone: false
})
export class CountdownComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  private countdownInterval: ReturnType<typeof setInterval> | undefined;
  overview: any;
  targetDate: number = 0;
  now = new Date().getTime();

  constructor(private http: HttpClient, private configurationService: ConfigurationService) {

  }

  get timeLeft(): number {
    const targetDate = this.targetDate ?? 0;
    const now = this.now ?? 0;
    return targetDate - now;
  }

  animationClass = { days: '', hours: '', minutes: '', seconds: '' };
  days = Math.floor(this.timeLeft / (1000 * 60 * 60 * 24));
  hours = Math.floor((this.timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  minutes = Math.floor((this.timeLeft % (1000 * 60 * 60)) / (1000 * 60));
  seconds = Math.floor((this.timeLeft % (1000 * 60)) / 1000);

  ngOnInit(): void {
    this.loadOverview();
    this.updateCountdown();
    this.countdownInterval = setInterval(() => {
      this.updateCountdown();
    }, 1000);
  }

  private safeParseOverview(val: any) {
    if (!val) return null;
    if (typeof val === 'string') {
      try {
        return JSON.parse(val);
      } catch {
        return val;
      }
    }
    return val;
  }

  loadOverview() {
    this.configurationService.getConfiguration().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(config => {
      const eo = this.safeParseOverview(config?.['eventOverview'] ?? config?.['overview'] ?? null);
      if (eo) {
        this.overview = eo;
        this.targetDate = new Date(this.overview.eventDate).getTime();
      } else {
        // No overview available from configuration; leave overview undefined
        this.overview = null;
        this.targetDate = 0;
      }
    });
  }

  updateCountdown() {
    const now = new Date().getTime();
    const timeLeft = this.targetDate - now;

    this.animateIfChanged('days', Math.floor(timeLeft / (1000 * 60 * 60 * 24)));
    this.animateIfChanged('hours', Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
    this.animateIfChanged('minutes', Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60)));
    this.animateIfChanged('seconds', Math.floor((timeLeft % (1000 * 60)) / 1000));

    if (this.days === 0 && this.hours === 0 && this.minutes === 0 && this.seconds === 0) {
      let confettiCount = 0;
      const confettiInterval = setInterval(() => {
        this.launchConfetti();
        confettiCount++;
        if (confettiCount >= 5) {
          clearInterval(confettiInterval);
        }
      }, 500); // Launch confetti every 0.5 second
      clearInterval(this.countdownInterval);
    }
  }

  animateIfChanged(unit: 'days' | 'hours' | 'minutes' | 'seconds', newValue: number) {
    if (this[unit] !== newValue) {
      this[unit] = newValue;
      this.animationClass[unit] = 'scale'; // Apply the animation
      setTimeout(() => {
        this.animationClass[unit] = ''; // Remove animation after it completes
      }, 300);
    }
  }

  launchConfetti() {
    confetti({
      particleCount: 300, // More particles for a big effect 🎊
      spread: 180, // Spread it out for a nice burst
      startVelocity: 50, // Make it pop!
    });
  }

  ngOnDestroy() {
    clearInterval(this.countdownInterval);
  }
}
